import { GeometryBuilder } from './geometry-builder';
import { WATER_LEVEL, type Terrain } from '../world/terrain';
import type { Vec } from '../destruction/structure';

/** Clip each actual ground triangle at the waterline. The water cannot cover
 * dry banks or finite river ends, and river/sea surfaces never overlap. */
export function terrainGeometry(terrain: Terrain): { land: GeometryBuilder; water: GeometryBuilder } {
  const land = new GeometryBuilder(),
    water = new GeometryBuilder(),
    v = terrain.vertices;
  for (let i = 0; i < terrain.indices.length; i += 3) {
    const triangle = Array.from(terrain.indices.slice(i, i + 3), (index) => ({
      x: v[index * 3],
      y: v[index * 3 + 1],
      z: v[index * 3 + 2],
    }));
    const [a, b, c] = triangle,
      ux = b.x - a.x,
      uy = b.y - a.y,
      uz = b.z - a.z,
      vx = c.x - a.x,
      vy = c.y - a.y,
      vz = c.z - a.z,
      nx = uy * vz - uz * vy,
      ny = uz * vx - ux * vz,
      nz = ux * vy - uy * vx,
      length = Math.hypot(nx, ny, nz),
      height = (a.y + b.y + c.y) / 3,
      z = (a.z + b.z + c.z) / 3;
    land.triangle(
      a,
      b,
      c,
      { x: nx / length, y: ny / length, z: nz / length },
      height < -0.25
        ? 0x637e76
        : height > 95
          ? 0xaab1a1
          : height > 25
            ? 0x84957c
            : terrain.coast && z > terrain.coast.beachStart
              ? 0xd7cba3
              : height < WATER_LEVEL
                ? 0xa5b299
                : 0xa7b58e,
    );
    const clipped: Vec[] = [];
    for (let j = 0; j < 3; j++) {
      const p = triangle[j],
        q = triangle[(j + 1) % 3],
        wet = p.y < WATER_LEVEL,
        nextWet = q.y < WATER_LEVEL;
      if (wet) clipped.push({ x: p.x, y: WATER_LEVEL, z: p.z });
      if (wet !== nextWet) {
        const t = (WATER_LEVEL - p.y) / (q.y - p.y);
        clipped.push({ x: p.x + (q.x - p.x) * t, y: WATER_LEVEL, z: p.z + (q.z - p.z) * t });
      }
    }
    for (let j = 1; j < clipped.length - 1; j++)
      water.triangle(clipped[0], clipped[j], clipped[j + 1], { x: 0, y: 1, z: 0 }, 0xffffff);
  }
  if (terrain.naturalBoundary) {
    // Ocean continues beyond the submerged mesh edge. Four non-overlapping
    // strips preserve a single water surface, including river/coast joins.
    const e = terrain.extent,
      f = 12000,
      p = (x: number, z: number) => ({ x, y: WATER_LEVEL, z }),
      n = { x: 0, y: 1, z: 0 };
    for (const [x, z, X, Z] of [
      [-f, -f, -e, f],
      [e, -f, f, f],
      [-e, -f, e, -e],
      [-e, e, e, f],
    ])
      water.quad(p(x, z), p(x, Z), p(X, Z), p(X, z), n, 0xffffff);
  }
  return { land, water };
}
